import { useState, useEffect, useMemo } from "react";

import {
  AlertOctagon,
  Apple,
  Book,
  BookMarked,
  BookOpenText,
  ChevronRight,
  ClipboardList,
  Clock,
  Crown,
  Globe,
  GraduationCap,
  LaptopMinimal,
  Microscope,
  MonitorCog,
  Pencil,
  PersonStanding,
  Pi
} from "lucide-react";
import { connect } from "react-redux";
import { toastr } from "react-redux-toastr";
import { Badge, Progress, Spinner } from "reactstrap";
import { bindActionCreators } from "redux";

import { logout } from "../../../actions/baseActions";
import Podium from "../../../assets/olympics-podium.png";
import ProtectedRoute from "../../../components/ProtectedRoute";
import URL from "../../../config/api";
import axios from "../../../config/axios";
import { friendlyDate } from "../../../utils/date";
import { formatString, getInitials } from "../../../utils/string";
import OlympicsLogo from "../Conecta.png";
import "./styles.css";

function OlympicsDashboard({ ...props }) {
  const olympicsId = props.match.params.id;

  const [olympicsData, setOlympicsData] = useState();
  const [progressData, setProgressData] = useState();
  const [podiumData, setPodiumData] = useState();
  const [activities, setActivities] = useState();
  const [currentTab, setCurrentTab] = useState("to-do");
  const [errorMessage, setErrorMessage] = useState();

  const allActivities = useMemo(() => {
    if (!activities) return [];
    return Object.values(activities).reduce(
      (acc, category) => [...acc, ...category],
      []
    );
  }, [activities]);

  const nextActivity = useMemo(() => {
    const categoryKey =
      activities?.["in-progress"]?.length > 0 ? "in-progress" : "to-do";

    return activities?.[categoryKey]?.sort(
      (a, b) => b.completion_percentage - a.completion_percentage
    )[0];
  }, [activities]);

  const totalCompletedActivities = useMemo(
    () => activities?.done?.length,
    [activities]
  );

  const totalCompletedItems = useMemo(
    () =>
      allActivities?.reduce(
        (acc, activity) => acc + activity.completion_percentage / 100,
        0
      ),
    [allActivities]
  );

  const totalCorrectAnswers = useMemo(
    () =>
      allActivities?.reduce(
        (acc, activity) => acc + activity.total_correct_answers,
        0
      ),
    [allActivities]
  );

  const totalItems = allActivities?.length;
  const totalActivities = allActivities?.length;

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!olympicsData) return;
      const newOlympicsData = {
        ...olympicsData,
        end: getDateData(olympicsData?.end?.date)
      };

      if (!newOlympicsData.color) newOlympicsData.color = "primary";

      setOlympicsData(newOlympicsData);
    }, 1000);

    checkOlympicsPermission();

    return () => clearInterval(intervalId);
  }, []);

  const checkOlympicsPermission = async () => {
    try {
      await axios.get(`${URL.api}olympics/verifypermission/${olympicsId}`, {
        headers: { Authorization: `Bearer ${props.user.token}` }
      });

      fetchOlympicsData();
      fetchProgressData();
      fetchActivitiesData();
    } catch (error) {
      toastr.error("Erro!", error.response.data.error);
      setErrorMessage(error.response.data.error);
    }
  };

  const getDateData = date => {
    const newDate = new Date(date);
    newDate.setHours(newDate.getHours() + 3);

    const simpleDate = [
      newDate.getFullYear(),
      newDate.getMonth() + 1,
      newDate.getDate()
    ]
      .reverse()
      .join("/");

    const daysLeft = Math.floor(
      (newDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );
    const hoursLeft = Math.floor(
      (newDate.getTime() - new Date().getTime()) / (1000 * 60 * 60)
    );
    const minutesLeft = Math.floor(
      (newDate.getTime() - new Date().getTime()) / (1000 * 60)
    );

    return {
      date: newDate,
      simpleDate,
      daysLeft,
      hoursLeft: hoursLeft % 24,
      minutesLeft: minutesLeft % 60
    };
  };

  const fetchOlympicsData = async () => {
    try {
      const olympicsDataResponse = await axios.get(
        `${URL.api}olympics/singleview/${olympicsId}`,
        {
          headers: { Authorization: `Bearer ${props.user.token}` }
        }
      );

      const parsedOlympicsData = {
        ...olympicsDataResponse.data.data.olympics,
        start: getDateData(olympicsDataResponse.data.data.olympics.start),
        end: getDateData(olympicsDataResponse.data.data.olympics.end)
      };

      setOlympicsData(parsedOlympicsData);
      setPodiumData(olympicsDataResponse.data.data.top_students);
    } catch (error) {
      toastr.error("Erro", "Erro ao buscar os dados da olimpíada.");
    }
  };

  const fetchProgressData = async () => {
    const checkinResponse = await axios.get(
      `${URL.api}olympics/checkin/${olympicsId}`,
      {
        headers: { Authorization: `Bearer ${props.user.token}` }
      }
    );
    try {
      const progressDataResponse = await axios.get(
        `${URL.api}olympics/progress/${olympicsId}`,
        {
          headers: { Authorization: `Bearer ${props.user.token}` }
        }
      );

      let progressScore = progressDataResponse.data.data?.score ?? 0;

      if (checkinResponse.data.data.first_checkin) {
        toastr.success(
          "Check-in",
          `Seu check-in diário já foi registrado e você ganhou ${
            checkinResponse.data.data.user_score.score - progressScore
          } pontos`
        );
      }

      const progressData = {
        ...progressDataResponse.data.data,
        score: checkinResponse.data.data.user_score.score
      };

      setProgressData(progressData);
    } catch (error) {
      toastr.error("Erro", "Erro ao buscar os dados do seu progresso.");
    }
  };

  const fetchActivitiesData = async () => {
    try {
      const activitiesDataResponse = await axios.get(
        `${URL.api}olympics/activity/list/${olympicsId}`,
        {
          headers: { Authorization: `Bearer ${props.user.token}` }
        }
      );

      const activitiesObj = {
        "to-do": activitiesDataResponse.data.data.filter(
          activity => activity.completion_percentage <= 0
        ),
        "in-progress": activitiesDataResponse.data.data.filter(
          activity =>
            activity.completion_percentage > 0 &&
            activity.completion_percentage < 100
        ),
        done: activitiesDataResponse.data.data.filter(
          activity => activity.completion_percentage === 100
        )
      };

      setActivities(activitiesObj);
    } catch (error) {
      setActivities([]);
      toastr.error("Erro", "Erro ao buscar os dados das atividades.");
    }
  };

  const handleActivityIcon = (disciplineName, color = "white", size = 24) => {
    switch (disciplineName) {
      case "Matemática":
        return <Pi size={size} className={`text-${color}`} />;
      case "Literatura":
        return <Book size={size} className={`text-${color}`} />;
      case "Português":
        return <Pencil size={size} className={`text-${color}`} />;
      case "Ciências":
        return <Microscope size={size} className={`text-${color}`} />;
      case "Física":
        return <Apple size={size} className={`text-${color}`} />;
      case "Geografia":
        return <Globe size={size} className={`text-${color}`} />;
      case "História":
        return <BookOpenText size={size} className={`text-${color}`} />;
      case "Educação Física":
        return <PersonStanding size={size} className={`text-${color}`} />;
      case "Religião":
        return <BookMarked size={size} className={`text-${color}`} />;
      case "Tecnologias da Informação e Comunicação":
        return <LaptopMinimal size={size} className={`text-${color}`} />;

      default:
        return <GraduationCap size={size} className={`text-${color}`} />;
    }
  };

  const handleActivityColor = disciplineName => {
    switch (disciplineName) {
      case "Matemática":
        return "info";
      case "Literatura":
        return "warning";
      case "Português":
        return "orange";
      case "Ciências":
        return "lime";
      case "Física":
        return "orange";
      case "Geografia":
        return "danger";
      case "História":
        return "primary";
      case "Educação Física":
        return "warning";
      case "Religião":
        return "danger";
      case "Tecnologias da Informação e Comunicação":
        return "purple";

      default:
        return "info";
    }
  };

  const handleSplicedDate = date => {
    const newDate = friendlyDate(date);
    return newDate.substring(0, newDate.indexOf(", às"));
  };

  return (
    <div className="container">
      {olympicsData ? (
        <>
          <section className="p-3 rounded-lg border d-flex flex-column mb-3">
            <div className="d-flex flex-column flex-md-row align-items-center mb-3">
              <div
                className={`olympics-dashboard-logo d-flex align-items-center justify-content-center rounded p-2 w-100 mb-3 mb-md-0 mr-xl-3 bg-${olympicsData?.color}`}
              >
                <img src={OlympicsLogo} alt="" style={{ objectFit: "cover" }} />
              </div>
              <div className="d-flex flex-column justify-content-between h-100 w-100">
                <div className="d-flex flex-column align-items-start mb-3">
                  <h3 className="mb-1 font-weight-bold">
                    {olympicsData?.name}
                  </h3>
                  <Badge color={olympicsData?.color} className="text-white">
                    {allActivities?.length === totalCompletedActivities
                      ? "Finalizada"
                      : "Em progresso"}
                  </Badge>
                </div>

                <div className="d-flex flex-column mb-2 w-100">
                  <div className="d-flex align-items-center justify-content-between">
                    <small className="text-muted">
                      {olympicsData?.start?.simpleDate} -{" "}
                      {olympicsData?.end?.simpleDate}
                    </small>
                    <small>
                      {Math.ceil((totalCompletedItems * 100) / totalItems)}%
                    </small>
                  </div>
                  <Progress
                    value={Math.ceil((totalCompletedItems * 100) / totalItems)}
                    color={olympicsData?.color}
                  />
                </div>
                <small className="text-muted w-100">
                  {olympicsData?.description}
                </small>
              </div>
            </div>

            {!(new Date(olympicsData?.end?.date) < new Date()) && (
              <div className="d-flex flex-column flex-xl-row align-items-center">
                <div className="d-flex flex-column w-100 mr-xl-2 mb-3 mb-xl-0">
                  <label className="font-weight-semibold text-dark">
                    Tempo restante
                  </label>
                  <div
                    className="d-flex align-items-center justify-content-center px-3 border rounded"
                    style={{ maxHeight: "72px", minHeight: "72px" }}
                  >
                    <div className="d-flex flex-column align-items-center justify-content-center">
                      <p className="font-weight-bold mb-0">
                        {olympicsData?.end?.daysLeft}
                      </p>
                      <small className="text-dark">Dias</small>
                    </div>
                    <h4 className="mb-0 mx-3 text-muted">:</h4>
                    <div className="d-flex flex-column align-items-center justify-content-center">
                      <p className="font-weight-bold mb-0">
                        {olympicsData?.end?.hoursLeft}
                      </p>
                      <small className="text-dark">Horas</small>
                    </div>
                    <h4 className="mb-0 mx-3 text-muted">:</h4>
                    <div className="d-flex flex-column align-items-center justify-content-center">
                      <p className="font-weight-bold mb-0">
                        {olympicsData?.end?.minutesLeft}
                      </p>
                      <small className="text-dark">Minutos</small>
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-column w-100 mx-xl-2 mb-3 mb-xl-0">
                  <label className="font-weight-semibold text-dark">
                    Pontuação
                  </label>
                  <div
                    className="d-flex align-items-center justify-content-center border rounded px-3"
                    style={{ maxHeight: "72px", minHeight: "72px" }}
                  >
                    <div className="d-flex flex-column align-items-center justify-content-center w-100">
                      <p className="font-weight-bold mb-0">Acesso</p>
                      <small className="text-dark">5pts</small>
                    </div>
                    <div
                      className="bg-light mx-3"
                      style={{
                        minWidth: "1px",
                        height: "2rem",
                        borderRadius: "0.5px"
                      }}
                    />
                    <div className="d-flex flex-column align-items-center justify-content-center w-100">
                      <p className="font-weight-bold mb-0">Atividade</p>
                      <small className="text-dark">20pts</small>
                    </div>
                    <div
                      className="bg-light mx-3"
                      style={{
                        minWidth: "1px",
                        height: "2rem",
                        borderRadius: "0.5px"
                      }}
                    />
                    <div className="d-flex flex-column align-items-center justify-content-center w-100">
                      <p className="font-weight-bold mb-0">Questão</p>
                      <small className="text-dark">10pts</small>
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-column w-100 h-100 ml-xl-2">
                  <label className="font-weight-semibold text-dark">
                    Próxima atividade
                  </label>
                  {nextActivity ? (
                    <a
                      href={`activity/${nextActivity?.activity_id}`}
                      className={`w-100 p-2 rounded d-flex align-items-center text-decoration-none bg-${handleActivityColor(
                        nextActivity?.discipline_name
                      )}-translucent`}
                      style={{ height: "72px" }}
                    >
                      <div
                        className={`p-2 h-100 mr-3 d-flex align-items-center rounded-sm justify-content-center bg-${handleActivityColor(
                          nextActivity?.discipline_name
                        )}`}
                        style={{ aspectRatio: "1/1" }}
                      >
                        {handleActivityIcon(
                          nextActivity?.discipline_name,
                          "white",
                          24
                        )}
                      </div>
                      <div className="d-flex flex-column justify-content-center w-100 h-100">
                        <div className="d-flex align-items-center w-100 mb-2">
                          <p
                            className="font-weight-normal m-0 text-nowrap text-dark"
                            title={nextActivity?.activity_name}
                          >
                            {nextActivity?.activity_name}
                          </p>
                        </div>
                        <div className="d-flex align-itens-center">
                          <small className="text-muted d-none d-md-flex text-nowrap align-items-center mr-2">
                            <ClipboardList size={12} className="mr-1" />
                            {nextActivity?.total_items} itens
                          </small>
                          <small
                            className="text-muted text-truncate d-none d-md-inline-block"
                            title={nextActivity?.discipline_name}
                            style={{ minWidth: "0" }}
                          >
                            <Book size={12} className="mr-1" />
                            {formatString(nextActivity?.discipline_name, 12)}
                          </small>
                        </div>
                      </div>
                      <div className="d-flex align-items-center justify-content-center ml-1 text-muted">
                        <ChevronRight size={18} />
                      </div>
                    </a>
                  ) : (
                    <div
                      className="d-flex align-items-center justify-content-center w-100 rounded bg-light text-muted"
                      style={{ height: "72px" }}
                    >
                      <small>Não há atividades pendentes no momento.</small>
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>

          {new Date(olympicsData?.end?.date) < new Date() ? (
            <section className="p-3 border rounded-lg d-flex flex-column mb-3">
              <div className="d-flex flex-column align-items-center justify-content-center">
                <MonitorCog size={64} />
                <h3>Prazo encerrado para realização das atividades</h3>
                <h6>
                  As pontuações finais estão sendo calculadas, e o resultado
                  será divulgado em 10/12.
                </h6>
              </div>
            </section>
          ) : (
            <>
              <section className="p-3 border rounded-lg d-flex flex-column mb-3">
                <label className="font-weight-semibold text-dark">
                  Seu progresso
                </label>
                <div className="olypics-dashboard-progress-report">
                  <div className="d-flex flex-column align-items-center justify-content-center w-100">
                    <h3 className="m-0 font-weight-semibold">
                      {progressData?.score || 0}
                    </h3>
                    <small className="text-muted">Pontos</small>
                  </div>

                  <div
                    className="bg-light mx-3 d-none d-md-block"
                    style={{
                      minWidth: "1px",
                      height: "4rem",
                      borderRadius: "0.5px"
                    }}
                  />

                  <div className="d-flex flex-column align-items-center justify-content-center w-100">
                    <h3 className="m-0 font-weight-semibold">
                      {progressData?.rank
                        ? `${progressData?.rank}º`
                        : "Sem colocação"}
                    </h3>
                    <small className="text-muted">
                      Posição no ranking geral - {progressData?.serie_name}
                    </small>
                  </div>

                  <div
                    className="bg-light mx-3 d-none d-md-block"
                    style={{
                      minWidth: "1px",
                      height: "4rem",
                      borderRadius: "0.5px"
                    }}
                  />

                  <div className="d-flex flex-column align-items-center justify-content-center w-100">
                    <h3 className="m-0 font-weight-semibold">
                      {totalCompletedActivities}/{allActivities?.length}
                    </h3>
                    <small className="text-muted">Atividades feitas</small>
                  </div>

                  <div
                    className="bg-light mx-3 d-none d-md-block"
                    style={{
                      minWidth: "1px",
                      height: "4rem",
                      borderRadius: "0.5px"
                    }}
                  />

                  <div className="d-flex flex-column align-items-center justify-content-center w-100">
                    <h3 className="m-0 font-weight-semibold">
                      {totalCorrectAnswers}/{totalActivities * 2}
                    </h3>
                    <small className="text-muted">Acertos</small>
                  </div>
                </div>
              </section>
              <section className="olympics-dashboard-footer mb-3">
                <div className="olympics-dashboard-podium border position-relative d-flex align-items-end justify-content-center rounded-lg bg-purple-semi-transparent">
                  <img
                    src={Podium}
                    alt=""
                    style={{ objectFit: "contain", maxWidth: "100%" }}
                    className=""
                  />
                  <div className="h-100 w-100 position-absolute d-flex flex-column align-items-start p-3">
                    <p className="text-dark font-weight-semibold mb-3">
                      Estudantes da escola em destaque
                    </p>
                    <div className="d-flex w-100 h-100 align-items-end justify-content-center pt-2">
                      <div
                        className="d-flex h-100 justify-content-center align-items-end"
                        style={{ width: "100%", maxWidth: "347px" }}
                      >
                        <div
                          style={{ marginBottom: "48%" }}
                          className="w-100 d-flex flex-column align-items-center justify-content-center"
                        >
                          <PodiumAvatar {...podiumData?.[1]} color="slate" />
                        </div>
                        <div
                          style={{ marginBottom: "58%" }}
                          className="w-100 d-flex flex-column align-items-center justify-content-center mx-2"
                        >
                          <Crown
                            size={24}
                            className="text-yellow d-none d-sm-block"
                          />
                          <PodiumAvatar {...podiumData?.[0]} color="yellow" />
                        </div>
                        <div
                          style={{ marginBottom: "38%" }}
                          className="w-100 d-flex flex-column align-items-center justify-content-center"
                        >
                          <PodiumAvatar {...podiumData?.[2]} color="orange" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="olympics-dashboard-activities p-3 border rounded-lg d-flex flex-column">
                  <div className="olmpics-dashboard-activities-tabs rounded mb-3">
                    <button
                      className={
                        currentTab === "to-do"
                          ? `rounded text-${olympicsData?.color} bg-${olympicsData?.color}-translucent`
                          : "text-muted bg-transparent transparent"
                      }
                      onClick={() => setCurrentTab("to-do")}
                    >
                      A fazer
                    </button>
                    <button
                      className={
                        currentTab === "in-progress"
                          ? `rounded text-${olympicsData?.color} bg-${olympicsData?.color}-translucent`
                          : "text-muted bg-transparent transparent"
                      }
                      onClick={() => setCurrentTab("in-progress")}
                    >
                      Em progresso
                    </button>
                    <button
                      className={
                        currentTab === "done"
                          ? `rounded text-${olympicsData?.color} bg-${olympicsData?.color}-translucent`
                          : "text-muted bg-transparent transparent"
                      }
                      onClick={() => setCurrentTab("done")}
                    >
                      Finalizadas
                    </button>
                  </div>
                  <div
                    className="h-100 overflow-auto"
                    style={{ maxHeight: "300px", minHeight: "300px" }}
                  >
                    {!activities ? (
                      <div className="d-flex align-items-center justify-content-center w-100 h-100 rounded bg-light text-muted">
                        <Spinner color="secondary" size="sm" className="mr-1" />
                        Carregando atividades...
                      </div>
                    ) : activities?.[currentTab]?.length === 0 ? (
                      <div className="d-flex align-items-center justify-content-center w-100 h-100 rounded bg-light text-muted">
                        {currentTab === "done"
                          ? "Você ainda não finalizou nenhuma atividade."
                          : currentTab === "to-do"
                          ? "Você não tem atividades pendentes."
                          : currentTab === "in-progress"
                          ? "Você não tem atividades em progresso."
                          : "Não há atividades no momento."}
                      </div>
                    ) : (
                      <ul className="d-flex flex-column list-unstyled p-0 m-0 h-100">
                        {activities?.[currentTab]?.map(activity => (
                          <a
                            key={activity.id}
                            href={`/olympics/activity/${activity.activity_id}`}
                            className={`w-100 h-100 p-2 rounded d-flex align-items-center text-decoration-none bg-${handleActivityColor(
                              nextActivity?.discipline_name
                            )}-translucent`}
                            style={{ maxHeight: "72px" }}
                          >
                            <div
                              style={{ aspectRatio: "1/1" }}
                              className={`p-2 h-100 mr-3 d-flex align-items-center rounded-sm justify-content-center bg-${handleActivityColor(
                                activity?.discipline_name
                              )}`}
                            >
                              {handleActivityIcon(
                                activity.discipline_name,
                                "white",
                                24
                              )}
                            </div>
                            <div className="d-flex flex-column w-100">
                              <div className="d-flex align-items-center w-100 mb-2">
                                <p
                                  className="font-weight-normal m-0 text-nowrap text-dark"
                                  title={activity.activity_name}
                                >
                                  {activity.activity_name}
                                </p>
                              </div>
                              <div className="d-flex align-itens-center">
                                <small className="text-muted d-none d-md-flex text-nowrap align-items-center mr-2">
                                  <ClipboardList size={12} className="mr-1" />
                                  {activity.total_items} itens
                                </small>
                                <small className="text-muted d-flex align-items-center text-nowrap mr-2">
                                  <Clock size={12} className="mr-1" />
                                  até{" "}
                                  {handleSplicedDate(activity.allocation_end)}
                                </small>
                                <small
                                  className="text-muted text-truncate d-none d-md-inline-block"
                                  title={activity.discipline_name}
                                  style={{ minWidth: "0" }}
                                >
                                  <Book size={12} className="mr-1" />
                                  {formatString(activity.discipline_name, 12)}
                                </small>
                              </div>
                            </div>
                          </a>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </section>{" "}
            </>
          )}
          <ProtectedRoute routeId={props.currentRoute} />
        </>
      ) : errorMessage ? (
        <div className="d-flex align-items-center justify-content-center w-100 h-100 pt-5">
          <AlertOctagon size={56} className="text-danger mr-2" />
          <div className="d-flex flex-column">
            <h3 className="mb-0 font-weight-bold text-danger">Atenção!</h3>
            <p className="mb-0 text-muted">{errorMessage}</p>
          </div>
        </div>
      ) : (
        <div className="d-flex align-items-center justify-content-center w-100 h-100 pt-5">
          <Spinner color="secondary" size="sm" className="mr-2" />
          Carregando dados da olimpíada...
        </div>
      )}
    </div>
  );
}

function PodiumAvatar({ name, photo, score = 0, color = "info" }) {
  return (
    <>
      {/* <img
        className="d-none d-md-flex rounded-circle bg-light mb-1 align-items-center justify-content-center w-50"
        style={{
          aspectRatio: "1 / 1",
          minWidth: "50px",
          objectFit: "cover"
        }}
        src={`${URL.img}${photo}`}
        alt={name}
      /> */}
      <div
        className={`d-none d-md-flex rounded-circle mb-1 align-items-center justify-content-center w-50 bg-${color}-translucent`}
        style={{
          aspectRatio: "1 / 1",
          minWidth: "50px"
        }}
      >
        <strong className={`text-${color}`}>{getInitials(name)}</strong>
      </div>
      <small className="text-dark m-0 font-weight-bold text-center mb-1">
        {name}
      </small>
      <Badge color="purple" className="text-white">
        {score}pts
      </Badge>
    </>
  );
}

const mapStateToProps = state => ({
  user: state.user,
  general: state.general,
  profile: state.profile,
  form: state.form
});

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      logout
    },
    dispatch
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(OlympicsDashboard);
